import { Stack, Typography, type SxProps } from '@mui/material';
import type { ResourceType } from '@repo/api-gw-sdk';
import { BackupPolicyType } from '@repo/api-gw-sdk';

import {
  apps,
  sourceAccountId,
  dataClasses,
  environment,
  resourceName,
  resourceType,
  sourceRegion,
  subnets,
  vpc,
  resourceId,
  cloudProvider,
  resourceGroupName,
  tagKeys,
  tagKeyValues,
} from '@/components/queryBuilder/properties';
import { ResourcesSelectionEditor } from '@/components/resourcesSelection/resourcesSelectionEditor';
import type { VaultManagedType } from '@/components/vaults/vaultManagedTypeSelect';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { RenameEntityName } from '@/components/wizard/renameEntityName';
import { ResourceTypes } from '@/data/inventory/resourceTypes';
import {
  hasValidConditionValues,
  type FilterProperty,
} from '@/types/advanceFilter';

import { type BackupPolicyEditState } from './BackupPolicyCreationFlow';
import { getPolicySupportedResourceTypes } from './BackupPolicyNameSelection';
import { CustomPolicySupportedDisclaimer } from './customPolicySupportedDisclaimer';
import { RestrictedResourceTypesSelector } from './restrictedResourceTypesSelector';

export const policySupportedProperties: FilterProperty[][] = [
  [resourceType, dataClasses, environment, apps],
  [
    cloudProvider,
    sourceAccountId,
    sourceRegion,
    vpc,
    subnets,
    resourceGroupName,
  ],
  [resourceName, resourceId],
  [tagKeys, tagKeyValues],
];

export const isRestrictedTypesPolicy = (
  policyType?: BackupPolicyType,
  vaultManagedType?: VaultManagedType
) =>
  (policyType &&
    (policyType === BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY ||
      policyType === BackupPolicyType.BACKUP_POLICY_TYPE_PITR)) ||
  vaultManagedType === 'self-managed-AWS';

export const BackupPolicyConditionEditor = (
  props: StepProps<BackupPolicyEditState>
) => {
  const nextButtonDisabledTooltip =
    props.wizardState.resourcesScopeType === 'useConditions' &&
    !hasValidConditionValues(props.wizardState.condition)
      ? 'Please create a condition with all required fields'
      : 'Please select at least one resource type';

  return (
    <>
      <StepContainer
        sx={{ padding: 0 }}
        stepperLabels={props.stepperLabels}
        stepperIndex={0}
        nextButtonDisabledTooltip={nextButtonDisabledTooltip}
        canGoNext={() =>
          (props.wizardState.policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD ||
            !!props.wizardState.resourceTypes?.length ||
            props.wizardState.resourcesScopeType === 'allExcluded') &&
          (props.wizardState.resourcesScopeType !== 'useConditions' ||
            hasValidConditionValues(props.wizardState.condition))
        }
        onNextClick={async () => {
          if (
            isRestrictedTypesPolicy(
              props.wizardState.policyType,
              props.wizardState.vaultManagedType
            ) &&
            props.wizardState.resourcesScopeType === 'allExcluded'
          ) {
            const resourceTypes = getPolicySupportedResourceTypes(
              props.wizardState.policyType,
              props.wizardState.vaultManagedType
            );
            props.setWizardState({
              ...props.wizardState,
              resourceTypes,
            });
          }
          props.setNextStep(props.currentStep.next?.[0]);
        }}
        navigationComponent={
          <RenameEntityName
            focusOnMount={props.wizardState.focusOnName}
            label='Backup policy name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
      >
        <CustomPolicySupportedDisclaimer
          policyType={props.wizardState.policyType}
          vaultManagedType={props.wizardState.vaultManagedType}
        />
        <ResourcesSelectionEditor
          selectedResourceTypes={props.wizardState.resourceTypes}
          setSelectedResourceTypes={(resourceTypes) =>
            props.setWizardState({ ...props.wizardState, resourceTypes })
          }
          isEditable={
            props.wizardState.policyType ===
            BackupPolicyType.BACKUP_POLICY_TYPE_HIGH_FREQUENCY
          }
          condition={props.wizardState.condition}
          setCondition={(condition) =>
            props.setWizardState({ ...props.wizardState, condition })
          }
          includedResources={props.wizardState.include}
          excludedResources={props.wizardState.exclude}
          setIncludedResources={(includedResources) =>
            props.setWizardState({
              ...props.wizardState,
              include: includedResources,
            })
          }
          setExcludedResources={(excludedResources) =>
            props.setWizardState({
              ...props.wizardState,
              exclude: excludedResources,
            })
          }
          resourcesScopeType={props.wizardState.resourcesScopeType}
          setResourcesScopeType={(resourcesScopeType) =>
            props.setWizardState({
              ...props.wizardState,
              resourcesScopeType,
            })
          }
          supportedProperties={policySupportedProperties}
          conditionTitle={
            isRestrictedTypesPolicy(props.wizardState.policyType) ? (
              <ConditionTitleWithResourceTypeSelector
                resourceTypes={props.wizardState.resourceTypes}
                restrictedResourceTypes={getPolicySupportedResourceTypes(
                  props.wizardState.policyType,
                  props.wizardState.vaultManagedType
                )}
                onChange={(resourceTypes) =>
                  props.setWizardState({ ...props.wizardState, resourceTypes })
                }
              />
            ) : (
              <BackupPolicyConditionTitle
                resourceTypes={props.wizardState.resourceTypes}
                sx={{ mb: '24px' }}
              />
            )
          }
          allExcludedDescription={
            'This backup policy won’t be automatically applied to any resources. Only manually included resources will be assigned to the policy.'
          }
          allIncludedDescription={
            isRestrictedTypesPolicy(props.wizardState.policyType)
              ? 'This backup policy will be automatically applied to all resources of the selected types. When new ones are discovered, they’ll be included too.'
              : 'This backup policy will be automatically applied to all resources. When new ones are discovered, they’ll be included too.'
          }
          manualOverridesDescription='Manually include or exclude resources from your backup policy, regardless of dynamic selection.'
        />
      </StepContainer>
    </>
  );
};

export const BackupPolicyConditionTitle = ({
  resourceTypes,
  sx,
}: {
  resourceTypes?: ResourceType[];
  sx?: SxProps;
}) => {
  return (
    <Typography variant='subtitle1' sx={sx}>
      Include{' '}
      {resourceTypes ? (
        <span className='font-medium'>
          {resourceTypes?.map((x) => ResourceTypes[x].displayName).join(', ')}
          {' resources '}
        </span>
      ) : (
        'resources '
      )}
      based on these conditions:
    </Typography>
  );
};

const ConditionTitleWithResourceTypeSelector = ({
  resourceTypes,
  restrictedResourceTypes,
  onChange,
}: {
  resourceTypes?: ResourceType[];
  restrictedResourceTypes?: ResourceType[];
  onChange: (resourceTypes: ResourceType[]) => void;
}) => {
  return (
    <Stack direction='row' alignItems='center' gap='8px' sx={{ mb: '24px' }}>
      <Typography variant='subtitle1'>Back up</Typography>
      {
        <RestrictedResourceTypesSelector
          resourceTypes={resourceTypes ?? []}
          restrictedResourceTypes={restrictedResourceTypes}
          onChange={onChange}
        />
      }
      <Typography variant='subtitle1'>
        resources based on these conditions:
      </Typography>
    </Stack>
  );
};
